import {
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  AfterViewInit,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  InputMaybe,
  Maybe,
  TixBusinessCodeGroup,
  TixBusinessCodes,
  TixBuyerProductFragmentFragment,
  TixContactObjRelInsertInput,
  TixContactOnConflict,
  TixCreatePaymentGQL,
  TixGetContactByEmailAddressGQL,
  TixGetDiscountInfoQuery,
  TixGetEventByEventInstanceIdGQL,
  TixGetFeesForVenueGQL,
  TixGetVenueByIdGQL,
  TixOrderInput,
  TixPaymentInsertInput,
  TixSendETicketGQL,
  TixVenuePortalEventFragment
} from '@tix/data-access';
import * as BuyerActions from '@tix/event-buyer/state';
import * as BuyerSelectors from '@tix/event-buyer/state';
import { TixBuyersPartialState } from '@tix/event-buyer/state';
import { TixCheckboxChange } from '@tix/shared/ui/components';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { environment } from '../../../../../../apps/purchasing-portal/src/environments/environment';
import { TextMaskConfig } from 'angular2-text-mask';

import { Location } from '@angular/common';
import {
  loadStripe,
  PaymentIntentResult,
  Stripe,
  StripeCardElement
} from '@stripe/stripe-js';
import { dollarFigureToNumber, formatAsDollarFigure } from '@tix/shared/utils';

import {
  BUSINESS_CODE_GROUP_NAMES,
  TIX_CONFIG_ITEM_IDS,
  TixBusinessGroupsService
} from '@tix/shared/state';

import { map, startWith, tap } from 'rxjs/operators';

import { BuyerProductService } from '@tix/event-buyer/components';
import {
  CheckoutOrderInfo,
  CustomerService,
  DiscountOrderItem,
  OrderItem,
  OrderStateManagerService,
  OrderTicket,
  PortalCheckoutService,
  PortalEvent,
  PortalEventsService,
  ProductOrderItem,
  TicketOrderItem,
  ReservationDetailsService,
  TixHiveService,
  MetaService,
  GoogleTagManagerService,
  DonationOrderItem,
  OrderPaymentService
} from '@tix/event-buyer/services';
import { TixVenuesService } from '@tix/venues/state';
import { OrderExpiryTimerComponent } from './order-expiry-timer';
import { HelperStringService } from '@tix/util-common';
import { OrderHoldService } from '@tix/order-hold';
import * as Sentry from '@sentry/angular-ivy';
import { concat, merge } from 'lodash';
import { AmplitudeService } from '@tix/core/analytics';
import {
  Order,
  Calculator,
  Discounts,
  onlineProcessingFeePreset,
  Ticket,
  Addon,
  Donation
} from 'asylumtix-calculator';
import { v4 as uuidv4 } from 'uuid';
import { DonationService } from 'libs/event-buyer/components/src/lib/services/donation.service';

// We add these fields to be able to group tickets and addons by their parent config id
export type ModifiedTicket = Ticket & { ticketConfigId: string };
// The `productConfigId` represents the `eventProductId`, the identifier of the record containing
// the addon's configuration
export type ModifiedAddon = Addon & { productConfigId: string };
export type ModifiedDonation = Donation & { comment: string };
export type ModifiedOrder = Omit<Order, 'tickets' | 'addons' | 'donation'> & {
  tickets: ModifiedTicket[];
  addons: ModifiedAddon[];
  donation?: ModifiedDonation;
};

interface PaymentFormValue {
  fname: string;
  lname: string;
  email: string;
  phoneNumber: string;
  dob?: string;
  cname: string;
  terms: boolean;
  sms: boolean;
  emails: boolean;
  address?: string;
  apartment?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
}

@Component({
  selector: 'tix-order-summary',
  templateUrl: './order-summary.component.html',
  styleUrls: ['./order-summary.component.scss'],
  providers: [OrderStateManagerService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TixOrderSummaryPage
  extends OrderExpiryTimerComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  calculatorService: Calculator;
  // start: addons variable
  addonsWithTax = 0;
  addonsNoTax = 0;
  products: (TixBuyerProductFragmentFragment & { selected: number })[] = [];

  addonsTax = 0;
  addonsSubtotal = 0;

  // helpers:
  #helperStringService = new HelperStringService();
  donationData?: { donation: number; comment: string };

  paymentForm = this.fb.group({
    fname: this.fb.control('', Validators.required),
    lname: this.fb.control('', Validators.required),
    email: this.fb.control('', [Validators.required, Validators.email]),
    phoneNumber: this.fb.control('', [
      Validators.required,
      Validators.minLength(12)
    ]),
    dob: this.fb.control(''),
    cname: this.fb.control('', Validators.required),
    // cardno: this.fb.control('', Validators.required),
    // expdate: this.fb.control('', Validators.required),
    // cvv: this.fb.control('', Validators.required),
    terms: this.fb.control('', Validators.required),
    sms: this.fb.control(true),
    emails: this.fb.control(true),
    address: this.fb.control(''),
    apartment: this.fb.control(''),
    city: this.fb.control(''),
    stateProvince: this.fb.control(''),
    postalCode: this.fb.control(''),
    country: this.fb.control('US')
  });
  getAmountPaid$ = new Subject<number>();
  stripekey = '';
  venueId = '';
  totalTickets = 0;
  buttonDisabled = false;
  invalidTerms = false;

  showLatencyMessage = false;

  loading = false;

  // sub: Subscription[];

  stripe: Stripe | null = null;
  cardElement: StripeCardElement | null = null;

  cardValid = false;
  isTestMode = false;
  discountError = '';

  feesTotal = 0;
  totalProcessingFee = 0;
  totalPlatformFee = 0;
  totalVenueFee = 0;
  totalWithNoFees = 0;
  ticketsSubtotal = 0;
  amountToPay = 0;
  getTotalAmount$: Observable<any>;
  appliedDiscount?: TixGetDiscountInfoQuery['discount'];
  discountToApply: any;
  flattenedTickets: any[] = [];
  discountAmount: any;
  addonsTotal: number;
  states: Maybe<
    Pick<TixBusinessCodeGroup, 'description' | 'name' | 'busCodeGroupId'> & {
      businessCodes: Pick<TixBusinessCodes, 'name' | 'description'>[];
    }
  >;

  filteredOptions$: Observable<
    Pick<TixBusinessCodes, 'name' | 'description'>[]
  >;

  countries: Maybe<
    Pick<TixBusinessCodeGroup, 'description' | 'name' | 'busCodeGroupId'> & {
      businessCodes: Pick<TixBusinessCodes, 'name' | 'description'>[];
    }
  >;

  venueHiveBrandId = '';
  venuePlatformFee: number;
  unUpdatedOrder: Order;
  event$: Observable<PortalEvent>;

  private onInitFinishedBS = new BehaviorSubject<boolean>(false);

  readonly PHONE_NUMBER_INPUT_MASK = [
    /[0-9a-zA-Z]/,
    /[0-9a-zA-Z]/,
    /[0-9a-zA-Z]/,
    '-',
    /[0-9a-zA-Z]/,
    /[0-9a-zA-Z]/,
    /[0-9a-zA-Z]/,
    '-',
    /[0-9a-zA-Z]/,
    /[0-9a-zA-Z]/,
    /[0-9a-zA-Z]/,
    /[0-9a-zA-Z]/
  ];

  readonly PHONE_NUMBER_INPUT_MASK_CONFIG: TextMaskConfig = {
    mask: this.PHONE_NUMBER_INPUT_MASK,
    guide: false
  };

  orderInfo?: CheckoutOrderInfo;
  tickets: OrderTicket[] = [];
  companyId: any;
  event: TixVenuePortalEventFragment;
  paymentError: string | undefined;

  trackItem(item: any) {
    return item.eventInstanceId;
  }

  get ticketsTotalWithNoFees() {
    return this.totalWithNoFees - this.addonsNoTax;
  }

  get subTotal() {
    return this.totalWithNoFees + this.feesTotal + this.addonsWithTax;
  }

  get canBuy() {
    if (this.appliedDiscount) {
      switch (this.appliedDiscount.groupDiscountType) {
        case 'A Minimum Order Of':
          if (this.totalTickets < this.appliedDiscount.groupDiscountAmount!) {
            this.discountError = `This discount code is not applicable for orders of less than ${this
              .appliedDiscount.groupDiscountAmount!} ${
              this.appliedDiscount.groupDiscountAmount! === 1
                ? ' ticket'
                : ' tickets'
            }.`;

            return false;
          }
          break;
        case 'A Maximum Order Of':
          if (this.totalTickets > this.appliedDiscount.groupDiscountAmount!) {
            this.discountError = `This discount code is not applicable for orders of more than ${this
              .appliedDiscount.groupDiscountAmount!} ${
              this.appliedDiscount.groupDiscountAmount! === 1
                ? ' ticket'
                : ' tickets'
            }.`;

            return false;
          }
          break;
        case 'An Order of Exactly':
          if (this.totalTickets !== this.appliedDiscount.groupDiscountAmount!) {
            this.discountError = `The discount code must be used for exactly ${this
              .appliedDiscount.groupDiscountAmount!} ${
              this.appliedDiscount.groupDiscountAmount! === 1
                ? ' ticket'
                : ' tickets'
            }.`;

            return false;
          }
          break;
      }
    }
    return this.totalTickets > 0;
  }

  get needCreditCard() {
    const result = this.canBuy && this.amountToPay > 0;
    if (result) {
      this.paymentForm.controls['cname'].setValidators(Validators.required);
      this.paymentForm.controls['cname'].updateValueAndValidity();
    } else {
      this.paymentForm.controls['cname'].setValidators(null);
      this.paymentForm.controls['cname'].updateValueAndValidity();
    }

    return result;
  }

  get donationAmount() {
    return this.donationData?.donation ?? 0;
  }

  ticketConfigMap: Record<string, string[]> = {};

  get orderTickets(): ModifiedTicket[] {
    const orderTickets = this.flattenedTickets.map(
      ticket =>
        ({
          id: ticket.id,
          venueFee: dollarFigureToNumber(ticket.venueFee),
          price: dollarFigureToNumber(ticket.ticketPrice),
          includeFeesInPrice: ticket.includeFees,
          ticketConfigId: ticket.ticketConfigId
        } as ModifiedTicket)
    );

    return orderTickets;
  }

  get orderAddons(): ModifiedAddon[] {
    const orderAddons = this.products
      .filter(p => p.selected > 0)
      .map(detail => {
        return Array.from(
          { length: detail.selected },
          () =>
            ({
              id: uuidv4(),
              price: this.dollarFigureToNumber(detail.price),
              tax: detail.tax,
              productConfigId: detail.eventProductId
            } as ModifiedAddon)
        );
      })
      .reduce((acc, curr) => acc.concat(curr), []);

    return orderAddons;
  }

  get order(): ModifiedOrder {
    let appliedDiscount;

    if (this.appliedDiscount?.type === '$ Discount') {
      appliedDiscount = new Discounts.StaticDiscount({
        amount: this.appliedDiscount.getAmount!,
        appliedTo:
          this.appliedDiscount?.appliedTo === 'Ticket'
            ? 'ticket'
            : 'reservation'
      });
    } else if (this.appliedDiscount?.type === '% Discount') {
      appliedDiscount = new Discounts.PercentageDiscount({
        amount: this.appliedDiscount.getAmount! / 100
      });
    } else if (this.appliedDiscount?.type === 'Buy X Get Y Free') {
      appliedDiscount = new Discounts.BuyXGetYFreeDiscount({
        freeAmount: this.appliedDiscount.getAmount!,
        buyAmount: this.appliedDiscount?.buyAmount!
      });
    } else if (this.appliedDiscount?.type === 'Spend $X, Get $Y Off') {
      appliedDiscount = new Discounts.SpendXGetYOffDiscount({
        offAmount: this.appliedDiscount.getAmount!,
        spendAmount: this.appliedDiscount?.buyAmount!
      });
    }

    return {
      tickets: this.orderTickets,
      addons: this.orderAddons,
      discount: appliedDiscount,
      donation: this.donationData
        ? {
            amount: this.donationData.donation,
            comment: this.donationData.comment
          }
        : undefined
    };
  }

  get includeFees(): boolean {
    return this.tickets.some(ticket => ticket.includeFees);
  }

  constructor(
    private _buyerProductService: BuyerProductService,
    // private TixGetCustomerByEmailGQL: TixGetCustomerByEmailGQL,
    private cd: ChangeDetectorRef,
    private store: Store<TixBuyersPartialState>,

    private readonly getVenueByID: TixGetVenueByIdGQL,

    cdr: ChangeDetectorRef,

    private snackbar: MatSnackBar,

    private router: Router,
    private zone: NgZone,

    private businessGroupsService: TixBusinessGroupsService,
    private hiveService: TixHiveService,

    private portalCheckoutService: PortalCheckoutService,
    private portalEventsService: PortalEventsService,

    location: Location,
    orderHoldService: OrderHoldService,
    ticketSelectionManager: BuyerSelectors.TicketSelectionManagerService,
    private fb: FormBuilder,
    private renderer: Renderer2,
    private metaService: MetaService,
    private googleTagManagerService: GoogleTagManagerService,
    private amplitudeService: AmplitudeService,
    private getFeesForVenueQuery: TixGetFeesForVenueGQL,
    private donationService: DonationService,

    private orderPaymentService: OrderPaymentService,
    private sendEticketMutation: TixSendETicketGQL
  ) {
    super(orderHoldService, snackbar, location, cdr);
  }

  get phoneNumberControl() {
    return this.paymentForm.controls['phoneNumber'] as FormControl;
  }

  dollarFigureToNumber(dollarFigure?: string | number): number {
    if (typeof dollarFigure === 'number') {
      return dollarFigure;
    }
    if (!dollarFigure) return 0;
    return Number(dollarFigure.replace(/[^0-9.-]+/g, ''));
  }

  async calculateAmounts() {
    const res = await this.getFeesForVenueQuery
      .fetch({
        venueId: this.venueId
      })
      .toPromise();

    this.venuePlatformFee =
      res.data.fees.filter(f => f.feeType === 'platform')[0]?.amount || 3;

    this.calculatorService = new Calculator({
      platformFee: { amount: this.venuePlatformFee },
      processingFee: onlineProcessingFeePreset,
      processingFeePrecision: 2
    });

    this.ticketsSubtotal = this.calculatorService.getTicketsSubtotal(
      this.order.tickets
    );
    this.addonsSubtotal = this.calculatorService.getAddonsSubtotal(
      this.order.addons
    );
    this.addonsTax = this.calculatorService.getAddonsTax(this.order.addons);

    this.discountAmount = this.calculatorService.getTotalDiscountAmount(
      this.order
    );
    const addonTax = this.calculatorService.getAddonsTax(this.orderAddons);
    this.totalWithNoFees =
      this.calculatorService.getTicketsSubtotal(this.orderTickets) +
      this.addonsTotal;

    this.totalProcessingFee = this.calculatorService.getProcessingFeeForOrder(
      this.order
    );
    this.totalPlatformFee = this.calculatorService.getPlatformFee(this.order);
    this.totalVenueFee = this.calculatorService.getVenueFee(this.order);
    this.addonsWithTax = addonTax + this.addonsTotal;
    this.addonsNoTax = this.addonsTotal;

    if (!this.includeFees) {
      this.feesTotal =
        this.totalProcessingFee + this.totalPlatformFee + this.totalVenueFee;
    } else {
      const addonsProcessingFee = this.order.addons.reduce(
        (total, addon) =>
          total +
          this.calculatorService.getAddonProcessingFee(this.order, addon),
        0
      );
      const donationProcessingFee =
        this.calculatorService.getProcessingFeeForDonation(this.order);
      this.feesTotal = addonsProcessingFee + donationProcessingFee;
    }

    // TODO: Check why the amountToPay is NaN when applying a 100% discount with included fees in tickets
    this.amountToPay = this.calculatorService.getTotalToPay(this.order) || 0;
  }

  override ngOnDestroy(): void {
    this.getAmountPaid$?.unsubscribe();

    super.ngOnDestroy();
  }

  trackButtonClick(eventName: string) {
    this.amplitudeService.trackEvent(`Button Clicked: ${eventName}`);
  }

  eventLoaded = false;

  override async ngOnInit() {
    super.ngOnInit();
    this.donationService.donation$.subscribe(donation => {
      this.donationData = donation;
    });

    this.venueId = this.router.url.split('/')[2].split('?')[0];

    const venue = await this.getVenueByID
      .fetch({
        venueId: this.venueId
      })
      .toPromise();

    this.companyId = venue.data.VenueByPK?.companyId;
    this.calculateAmounts();

    this.businessGroupsService
      .getBusinessGroupByName(BUSINESS_CODE_GROUP_NAMES.STATE_PROVINCE)
      .toPromise()
      .then(res => {
        this.states = res;
      });

    this.businessGroupsService
      .getBusinessGroupByName(BUSINESS_CODE_GROUP_NAMES.COUNTRY_CODE)
      .toPromise()
      .then(res => {
        this.countries = res;
      });

    this.filteredOptions$ =
      this.paymentForm.get('stateProvince')?.valueChanges.pipe(
        startWith(''),
        map(value => this._filter(value || ''))
      ) || of([]);

    const savedTicketConfigData = JSON.parse(
      sessionStorage.getItem('eventTicketsOrder') ?? 'null'
    );
    const order = this.portalCheckoutService.getPersistedOrderInfo();

    if (order) {
      this.totalWithNoFees = this.dollarFigureToNumber(order.total);
      this.orderInfo = order;

      this.onInitFinishedBS.next(true);
      if (order.discount) this.appliedDiscount = order.discount;

      this.tickets = order.tickets;

      this.calculateTotalTickets();

      this.event$ = this.portalEventsService
        .getEventById(order.eventInstanceId)
        .pipe(
          tap(event => {
            this.eventLoaded = true;
            this.event = event;
          })
        );
    }

    if (!this.needCreditCard) {
      this.paymentForm.controls['cname'].setValidators(null);
      this.paymentForm.controls['cname'].updateValueAndValidity();
    }

    if (venue.data.VenueByPK?.state.toLowerCase() === 'active') {
      console.log(venue);
      this.stripekey = environment.liveStripeKey;
      this.isTestMode = false;
    } else {
      this.stripekey = environment.testStripeKey;
      this.isTestMode = true;
    }

    // loading stripe
    this.loadStripe();

    this.calculateAmounts();

    this.venueHiveBrandId =
      venue?.data.VenueByPK?.configurationValues.find(
        config =>
          config.Value?.configurationItemId ===
          TIX_CONFIG_ITEM_IDS.VENUE_HIVE_BRAND_ID
      )?.Value?.value ?? '';

    if (this.venueHiveBrandId) {
      this.hiveService.init(this.venueHiveBrandId);
    }
  }

  updateProducts(
    products: (TixBuyerProductFragmentFragment & { selected: number })[]
  ) {
    this.products = products;
    this.calculateAmounts();
  }

  increaseProduct(products: any[]) {
    this.products = products;
    this.calculateAmounts();
  }

  ngAfterViewInit() {
    this.onInitFinishedBS.asObservable().subscribe(async initialized => {
      if (initialized) {
        const event = await this.portalEventsService
          .getEventById(this.orderInfo?.eventInstanceId as string)
          .toPromise();
        this.metaService.pixels = event.campaign;
        this.metaService.injectMetaPixel();
        this.googleTagManagerService.pixels = event.campaign;
        this.getAmountPaid$.subscribe({
          next: value => {
            this.calculateTotalTickets();
            this.googleTagManagerService.injectGTM({
              eventName: event.eventName || '',
              venueName: event.venue?.name || '',
              localDate: new Date(),
              totalAmount: `${value}`,
              ticketsQuantity: this.totalTickets,
              productsQuantity: this.products
                .filter(p => p.selected > 0)
                .reduce((sum, product) => sum + product.selected, 0),
              value: value,
              currency: 'USD'
            });
          }
        });
      }
    });
  }

  calculateTotalTickets(): void {
    this.totalTickets = this.tickets.reduce(
      (acc: number, curr: any) => acc + (curr.ticketQty || 0),
      0
    );
  }

  async loadStripe() {
    this.stripe = await loadStripe(this.stripekey);

    this.cardElement = this.stripe?.elements().create('card') || null;
    this.cardElement?.mount('#card-element');
    this.cardElement?.on('change', event => {
      this.cardValid = event.complete;
    });
    this.cardElement?.on('focus', () => {
      document.getElementById('card-element')?.classList.add('focused');
    });

    this.cardElement?.on('blur', () => {
      document.getElementById('card-element')?.classList.remove('focused');
    });
  }

  async onChange(tickets: OrderTicket[]) {
    this.tickets = tickets;

    this.flattenedTickets = this.orderInfo!.tickets.filter(
      ticket => ticket.ticketQty
    )
      .map(ticket =>
        Array(ticket.ticketQty)
          .fill(null)
          .map(() => ({
            ...ticket,
            quantity: 1,
            id: uuidv4()
          }))
      )
      .reduce((acc, curr) => acc.concat(curr), []);

    if (this.orderInfo) {
      this.orderInfo = { ...this.orderInfo, tickets };
    }
    this.portalCheckoutService.persistOrderInfo(this.orderInfo!);

    await this.calculateAmounts();
    this.getAmountPaid$.next(this.amountToPay);
    this.calculateTotalTickets();
  }

  hasError(control: string, error: string): boolean {
    return this.paymentForm.get(control)?.hasError(error) ?? false;
  }

  checkTerms(event: TixCheckboxChange) {
    if (!event.checked) {
      this.invalidTerms = true;
    } else {
      this.invalidTerms = false;
    }
  }

  checkoutFailure(message?: string) {
    if (message) {
      this.zone.run(() => {
        this.snackbar.open(message, 'Close', {
          duration: 5000,
          verticalPosition: 'bottom',
          horizontalPosition: 'center'
        });
      });
    } else {
      this.zone.run(() => {
        this.snackbar.open('An error has ocurred', 'Close', {
          duration: 5000,
          verticalPosition: 'bottom',
          horizontalPosition: 'center'
        });
      });
    }
    this.buttonDisabled = false;
    this.loading = false;
    this.cd.detectChanges();
  }

  private getOrderInput() {
    const orderInput: TixOrderInput = {
      tickets: this.order.tickets.map(ticket => ({
        ticketId: ticket.id,
        ticketConfigId: ticket.ticketConfigId
      })),
      products: this.order.addons.map(addon => ({
        id: addon.id,
        eventProductId: addon.productConfigId
      })),
      donation:
        this.donationAmount && this.donationData?.comment
          ? {
              amount: this.donationAmount,
              comment: this.donationData.comment
            }
          : undefined,
      appliedDiscountId: this.appliedDiscount?.discountId
    };

    return orderInput;
  }

  async verifyAmountToPay() {
    const orderInput = this.getOrderInput();
    const localAmountToPay =
      this.calculatorService.getTotalToPay(this.order) || 0;
    const realAmountToPay =
      await this.orderPaymentService.getAmountToPayForOrder({
        order: orderInput,
        eventInstanceId: this.event.eventInstanceId
      });

    alert(`local: ${localAmountToPay}. server: ${realAmountToPay}`);
  }

  async onSubmit(form: PaymentFormValue) {
    try {
      if (!this.canBuy) return;

      // this.amountToPay = this.amountToPay + this.#calculateProductsPriceWithTax();

      this.buttonDisabled = true;
      this.loading = true;
      this.paymentError = undefined;

      setTimeout(() => {
        this.showLatencyMessage = this.loading;
      }, 3000);

      // stop here if form is invalid
      if (this.paymentForm.invalid) {
        if (!this.paymentForm.value.terms) {
          this.invalidTerms = true;
        }
        this.buttonDisabled = false;
        this.loading = false;
        return;
      }

      const totalToPay = this.calculatorService.getTotalToPay(this.order);

      let paymentIntentId: string | undefined;
      let paymentClientSecret: string | undefined;

      const orderInput = this.getOrderInput();

      if (totalToPay) {
        const res = await this.orderPaymentService
          .createPaymentIntentForOrder({
            eventInstanceId: this.event.eventInstanceId,
            order: orderInput
          })
          .toPromise();
        if (!res) {
          this.checkoutFailure('An error has ocurred');
          return;
        }

        paymentClientSecret = res.clientSecret;
        paymentIntentId = res.paymentIntentId;
      }

      const reservationId = await this.orderPaymentService
        .createUnconfirmedReservation({
          eventInstanceId: this.event.eventInstanceId,
          order: orderInput,
          paymentIntentId,
          customer: {
            emailAddress: form.email,
            firstName: form.fname,
            lastName: form.lname,
            phoneNumber: form.phoneNumber,
            city: form.city,
            countryCode: form.country,
            dob: form.dob,
            emails: form.emails,
            postalCode: form.postalCode,
            sms: form.sms,
            stAddress: form.address,
            stateProvince: form.stateProvince,
            suiteApartment: form.apartment
          }
        })
        .toPromise();

      if (!reservationId) throw new Error('Something went wrong');

      if (totalToPay) {
        const result = await this.stripe!.confirmCardPayment(
          paymentClientSecret!,
          {
            payment_method: {
              card: this.cardElement!,
              billing_details: {
                name: form.fname + ' ' + form.lname,
                email: form.email,
                phone:
                  '+1' +
                  form.phoneNumber
                    .split('')
                    .filter((char: string) => char !== '-')
                    .join(''),
                address: {
                  city: form.city,
                  country: form.country,
                  state: form.stateProvince,
                  postal_code: form.postalCode,
                  line1: form.address
                }
              }
            }
          }
        );

        if (result.error) {
          // this.checkoutFailure(result.error.message);
          this.paymentError = result.error.message;
        } else {
          this.trackCreditCardPaymentEvent();
          this.logCustomerToHive(form);
        }
      }

      const success = await this.orderPaymentService
        .confirmReservation({ reservationId: reservationId, paymentIntentId })
        .toPromise();
      if (!success) {
        alert(
          'Please contact support@asylumtix.com with the code: ' + reservationId
        );
        throw new Error('An error has ocurred');
      }

      this.router.navigate(['/success', this.venueId, reservationId]);
    } catch (error) {
      this.checkoutFailure(
        error instanceof Error ? error.message : 'An error has ocurred'
      );
      this.logCheckoutError(error);
    }
  }

  logCustomerToHive(form: PaymentFormValue) {
    if (this.venueHiveBrandId) {
      this.hiveService.logCustomer({
        email: form.email,
        firstName: form.fname,
        lastName: form.lname,
        birthday: form.dob || undefined,
        didOptIn: form.emails,
        didSmsOptIn: form.sms,
        phoneNumber:
          '+1' +
          form.phoneNumber
            .split('')
            .filter((char: string) => char !== '-')
            .join(''),
        city: form.city,
        country: form.country,
        location: form.address,
        state: form.stateProvince,
        zipCode: form.postalCode,
        mailingAddress: form.address
      });
    }
  }

  trackCreditCardPaymentEvent() {
    this.amplitudeService.trackEvent('Credit Card Payment', {
      venueName: this.event.venue?.name as string,
      eventName: this.event.eventName as string,
      discountAmount: formatAsDollarFigure(this.discountAmount)
    });

    this.metaService.trackPurchaseEvent({
      eventName: this.event.eventName as string,
      venueName: this.event.venue?.name as string,
      localDate: new Date(),
      totalAmount: formatAsDollarFigure(
        parseFloat(this.amountToPay.toFixed(2))
      ),
      ticketsQuantity: this.orderInfo?.tickets.length as number,
      productsQuantity: this.order.addons.length,
      value: parseFloat(this.amountToPay.toFixed(2)),
      currency: 'USD',
      eventDate: this.event.date,
      eventTime: this.event.startTime
    });
  }

  trackFreeReservationEvent() {
    this.amplitudeService.trackEvent('Free reservation', {
      eventInstanceId: this.orderInfo?.eventInstanceId,
      amount: formatAsDollarFigure(parseFloat(this.amountToPay.toFixed(2))),
      ticketsQuantity: this.orderInfo!.tickets.reduce(
        (sum, ticket) => sum + ticket.ticketQty,
        0
      ),
      discountAmount: formatAsDollarFigure(this.discountAmount)
    });
  }

  logCheckoutError(err: any) {
    const eventId = Sentry.captureEvent({
      message: 'Checkout Error: ' + err,
      level: 'error',
      extra: { failed_input_data: this.orderInfo }
    });
  }

  private _filter(
    value: string
  ): Pick<TixBusinessCodes, 'name' | 'description'>[] {
    const filterValue = value.toLowerCase();
    const reMatch = new RegExp(`${filterValue}`, 'i');
    return (
      this.states?.businessCodes.filter(option =>
        option?.description?.match(reMatch)
      ) || []
    );
  }
}
